import React, { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { Section, SectionHeading } from "@/components/blocks";
import api from "@/lib/api";

const Initials = ({ name }) => (
  <span className="w-11 h-11 rounded-full bg-teal-600 text-white font-display font-bold flex items-center justify-center shrink-0 text-sm">
    {name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
  </span>
);

const Stars = ({ rating = 5, id }) => (
  <span className="flex items-center gap-0.5" aria-label={`${rating} out of 5`} data-testid={`testimonial-rating-${id}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} aria-hidden />
    ))}
  </span>
);

const ServiceChip = ({ service, light, id }) =>
  service ? (
    <span
      data-testid={`testimonial-service-${id}`}
      className={`inline-block mt-2 text-[11px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full ${
        light ? "bg-teal-500/15 text-teal-300" : "bg-teal-50 text-teal-700"
      }`}
    >
      {service}
    </span>
  ) : null;

const Body = ({ t, light, large = false }) => (
  <>
    <div className="flex items-center justify-between mb-5">
      <Stars rating={t.rating} id={t.id} />
      <Quote className={`w-7 h-7 ${light ? "text-slate-600" : "text-teal-200"}`} aria-hidden />
    </div>
    <blockquote
      className={`font-serif-editorial italic leading-relaxed ${large ? "text-lg sm:text-xl" : "text-[17px]"} ${
        light ? "text-slate-200" : "text-slate-700"
      }`}
    >
      “{t.quote}”
    </blockquote>
    <figcaption className={`mt-auto pt-6 border-t flex items-start gap-3 ${light ? "border-slate-700" : "border-slate-100"}`}>
      <Initials name={t.name} />
      <span>
        <span className={`block font-semibold ${light ? "text-white" : "text-slate-900"}`}>{t.name}</span>
        <span className={`block text-sm ${light ? "text-slate-400" : "text-slate-500"}`}>{t.role}</span>
        <ServiceChip service={t.service} light={light} id={t.id} />
      </span>
    </figcaption>
  </>
);

/** Testimonials. Single entry renders centred; multiple entries auto-scroll as a marquee. */
export default function Testimonials({ category, light = false, heading = "What educators say", eyebrow = "Testimonials", subtitle }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const shown = category ? items.filter((t) => t.category === category) : items;
  if (shown.length === 0) return null;

  if (shown.length === 1) {
    const t = shown[0];
    return (
      <Section>
        <SectionHeading eyebrow={eyebrow} title={heading} subtitle={subtitle} light={light} center />
        <div className="max-w-3xl mx-auto" data-testid="testimonials-section">
          <figure
            data-testid={`testimonial-${t.id}`}
            className={`flex flex-col rounded-2xl border p-8 sm:p-10 ${light ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}
          >
            <Body t={t} light={light} large />
          </figure>
        </div>
      </Section>
    );
  }

  const loop = [...shown, ...shown];

  return (
    <Section>
      <SectionHeading eyebrow={eyebrow} title={heading} subtitle={subtitle} light={light} />
      <div className="tp-marquee" data-testid="testimonials-section">
        <div className="tp-marquee-track" style={{ animationDuration: `${Math.max(28, shown.length * 16)}s` }}>
          {loop.map((t, i) => (
            <figure
              key={`${t.id}-${i}`}
              data-testid={`testimonial-${t.id}`}
              className={`w-[320px] sm:w-[440px] shrink-0 whitespace-normal rounded-2xl border p-8 flex flex-col ${
                light ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200 shadow-sm"
              }`}
            >
              <Body t={t} light={light} />
            </figure>
          ))}
        </div>
      </div>
    </Section>
  );
}
